import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../api/client';
import type { AuthConfigResponse, MeResponse } from './types';

const ME_KEY = ['me'] as const;
const AUTH_CONFIG_KEY = ['auth-config'] as const;

export function useAuthConfig() {
  return useQuery<AuthConfigResponse>({
    queryKey: AUTH_CONFIG_KEY,
    queryFn: () => api<AuthConfigResponse>('/api/auth/config'),
    staleTime: Infinity,
  });
}

/**
 * Carga /api/me sin redirigir en 401 — devuelve null cuando no hay sesión.
 * Los componentes que necesiten auth usan RequireAuth para hacer el gating.
 */
export function useMe() {
  return useQuery<MeResponse | null>({
    queryKey: ME_KEY,
    queryFn: async () => {
      try {
        return await api<MeResponse>('/api/me', { silent401: true });
      } catch (err) {
        // 401 → devolvemos null; otros errores los propagamos.
        if (err instanceof Error && err.message === 'No autenticado') return null;
        throw err;
      }
    },
    staleTime: 30_000,
  });
}

export function useLogout() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api('/api/auth/logout', { method: 'POST' }),
    onSuccess: () => {
      qc.setQueryData(ME_KEY, null);
      window.location.href = '/login';
    },
  });
}

export function useLoginMock() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (params: { id: string; next?: string }) =>
      api<{ ok: true; redirectTo: string }>('/api/auth/mock/login', {
        method: 'POST',
        body: params,
      }),
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ME_KEY });
      window.location.href = data.redirectTo;
    },
  });
}
